<template>
  <div class="navbar-search-location" :class="rootClass">
    <button
      class="location-button inline-flex h-10 w-full min-w-0 shrink-0 items-center gap-2 rounded-xl bg-slate-50 px-3 text-sm font-normal text-slate-950 transition hover:bg-slate-100 sm:w-auto sm:max-w-[12rem]"
      type="button"
      @click="$emit('open-location')"
    >
      <i class="location-icon ri-map-pin-2-line h-5 w-5 shrink-0 text-orange-600" aria-hidden="true"></i>
      <span class="location-text truncate">{{ displayLocation }}</span>
    </button>

    <label class="search-field relative w-full min-w-[12rem] flex-1">
      <span class="sr-only">Search</span>
      <i class="search-icon ri-search-line absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" aria-hidden="true"></i>
      <input
        :value="search"
        class="h-10 w-full rounded-xl border-0 bg-slate-50 px-10 text-sm font-medium text-slate-950 outline-none transition placeholder:text-slate-500 focus:bg-white focus:ring-2 focus:ring-indigo-100"
        type="search"
        placeholder="Search"
        @input="$emit('update:search', $event.target.value)"
        @keyup.enter="$emit('submit-search')"
        @focus="searchFocused = true"
        @blur="hideSuggestions"
      />

      <div
        v-if="showSuggestions"
        class="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-50 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-950/10"
      >
        <div v-if="loading" class="px-4 py-3 text-sm font-medium text-slate-500">
          Searching flyers...
        </div>

        <template v-else-if="suggestions.length">
          <button
            v-for="suggestion in suggestions"
            :key="suggestion.id"
            class="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-slate-50"
            type="button"
            @mousedown.prevent="$emit('select-suggestion', suggestion)"
          >
            <img
              v-if="suggestion.image"
              class="h-10 w-10 shrink-0 rounded-xl bg-slate-100 object-cover"
              :src="suggestion.image"
              :alt="suggestion.title"
              loading="lazy"
            />
            <span v-else class="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-orange-50 text-sm font-bold text-orange-700">
              {{ suggestion.initial }}
            </span>
            <span class="min-w-0 flex-1">
              <span class="block truncate text-sm font-bold text-slate-950">{{ suggestion.title }}</span>
              <span class="mt-0.5 block truncate text-xs font-medium text-slate-500">{{ suggestion.meta }}</span>
            </span>
          </button>
        </template>

        <div v-else class="px-4 py-3 text-sm font-medium text-slate-500">
          No flyers found.
        </div>
      </div>
    </label>

    <slot name="actions" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  search: { type: String, default: '' },
  displayLocation: { type: String, default: 'Choose location' },
  suggestions: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  rootClass: { type: String, default: 'hidden min-w-0 flex-1 items-center gap-2 xl:flex xl:max-w-[44rem]' }
})

defineEmits(['update:search', 'submit-search', 'open-location', 'select-suggestion'])

const searchFocused = ref(false)
let blurTimer

const showSuggestions = computed(() => searchFocused.value && props.search.trim().length >= 2)

const hideSuggestions = () => {
  clearTimeout(blurTimer)
  blurTimer = setTimeout(() => {
    searchFocused.value = false
  }, 120)
}
</script>

<style scoped>
@media (max-width: 1279px) {
  .mobile-search-location {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.55rem;
    min-width: min(100%, 14rem);
    border: 0;
    background: transparent;
    padding: 0;
  }

  .mobile-search-location .search-field {
    order: 1;
    min-width: 0;
  }

  .mobile-search-location .location-button {
    order: 2;
    justify-self: end;
    width: auto;
    max-width: min(100%, 17rem);
    min-height: 2.35rem;
    gap: 0.45rem;
    border: 1px solid rgb(226 232 240);
    border-radius: 999px;
    background: rgb(255 255 255);
    padding: 0.35rem 0.75rem 0.35rem 0.45rem;
    color: rgb(30 41 59);
    font-size: 0.84rem;
    font-weight: 650;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  }

  .mobile-search-location .location-button:hover {
    border-color: rgb(199 210 254);
    background: rgb(248 250 252);
    color: rgb(30 41 59);
  }

  .mobile-search-location .location-icon {
    height: 1.45rem;
    width: 1.45rem;
    border-radius: 999px;
    background: rgb(238 242 255);
    color: rgb(79 70 229);
    fill: rgb(79 70 229);
    padding: 0.28rem;
  }

  .mobile-search-location .location-text {
    max-width: 12rem;
    text-decoration: none;
  }

  .mobile-search-location .search-field input {
    height: 2.6rem;
    border-radius: 999px;
    background: rgb(255 255 255);
    border: 1px solid rgb(226 232 240);
    padding-left: 2.65rem;
    padding-right: 1rem;
    font-size: 0.95rem;
    font-weight: 500;
    color: rgb(51 65 85);
  }

  .mobile-search-location .search-field input:focus {
    background: rgb(255 255 255);
    border-color: rgb(199 210 254);
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12);
  }

  .mobile-search-location .search-icon {
    left: 0.9rem;
    height: 1.05rem;
    width: 1.05rem;
    color: rgb(148 163 184);
  }

  .mobile-search-location.explore-search-location {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    min-width: 0;
    align-items: center;
    gap: 0.4rem 0.5rem;
  }

  .mobile-search-location.explore-search-location .search-field {
    grid-column: 1 / -1;
    grid-row: 1;
    min-width: 0;
  }

  .mobile-search-location.explore-search-location .search-field input {
    height: 2.25rem;
    padding-left: 2.15rem;
    padding-right: 0.65rem;
    font-size: 0.82rem;
  }

  .mobile-search-location.explore-search-location .search-icon {
    left: 0.72rem;
    display: grid;
    height: 1rem;
    width: 1rem;
    place-items: center;
    font-size: 0.9rem;
  }

  .mobile-search-location.explore-search-location .location-button {
    grid-column: 1;
    grid-row: 2;
    width: auto;
    max-width: min(100%, 12rem);
    min-height: 2.25rem;
    justify-self: start;
    gap: 0.3rem;
    padding: 0.25rem 0.55rem 0.25rem 0.3rem;
    font-size: 0.75rem;
    box-shadow: none;
  }

  .mobile-search-location.explore-search-location .location-icon {
    display: grid;
    width: 1.55rem;
    height: 1.55rem;
    place-items: center;
    padding: 0;
    font-size: 0.85rem;
  }

  .mobile-search-location.explore-search-location .location-text {
    max-width: 9rem;
  }

  .mobile-search-location.explore-search-location :slotted(.explore-layout-switch) {
    grid-column: 2;
    grid-row: 2;
    align-self: center;
    justify-self: end;
  }
}
</style>
